import { Modal } from '@mantine/core';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, InputField, TextArea } from '@xver/components/Atoms';

const SSH_KEY_NAME_REGEX = /^[a-zA-Z0-9-_]{4,20}$/;

interface AddSSHKeyModalProps {
    opened: boolean;
    onClose: () => void;
    onSubmit: (payload: { name: string; content: string }) => void | Promise<unknown>;
    isSubmitting?: boolean;
}

interface FormErrors {
    name?: string;
    content?: string;
}

type FormTouched = Partial<Record<keyof FormErrors, boolean>>;

export default function AddSSHKeyModal({
    opened,
    onClose,
    onSubmit,
    isSubmitting,
}: AddSSHKeyModalProps) {
    const { t } = useTranslation('sshKeys');

    const [name, setName] = useState('');
    const [content, setContent] = useState('');
    const [touched, setTouched] = useState<FormTouched>({});
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const nameRef = useRef<HTMLInputElement>(null);
    const contentRef = useRef<HTMLTextAreaElement>(null);

    const validation = useMemo<FormErrors>(() => {
        const errors: FormErrors = {};
        const trimmedName = name.trim();
        if (!trimmedName) errors.name = t('errors.nameRequired');
        else if (!SSH_KEY_NAME_REGEX.test(trimmedName)) errors.name = t('errors.nameInvalid');
        if (!content.trim()) errors.content = t('errors.contentRequired');
        return errors;
    }, [name, content, t]);

    const visibleErrors = useMemo<FormErrors>(() => {
        const out: FormErrors = {};
        if (validation.name && (touched.name || submitAttempted)) out.name = validation.name;
        if (validation.content && (touched.content || submitAttempted))
            out.content = validation.content;
        return out;
    }, [validation, touched, submitAttempted]);

    const handleSubmit = async () => {
        if (!submitAttempted) setSubmitAttempted(true);
        if (validation.name || validation.content) {
            (validation.name ? nameRef : contentRef).current?.focus();
            return;
        }
        await onSubmit({ name: name.trim(), content: content.trim() });
        setName('');
        setContent('');
        setTouched({});
        setSubmitAttempted(false);
    };

    const handleClose = () => {
        if (isSubmitting) return;
        setName('');
        setContent('');
        setTouched({});
        setSubmitAttempted(false);
        onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={<span className="text-lg font-bold">{t('addModal.title')}</span>}
            centered
            size="lg"
        >
            <div className="flex flex-col gap-4">
                <InputField
                    ref={nameRef}
                    size="md"
                    label={t('addModal.name')}
                    placeholder={t('addModal.namePlaceholder')}
                    description={t('addModal.nameDescription')}
                    value={name}
                    onChange={(e) => {
                        setName(e.target.value);
                        if (!touched.name) setTouched((p) => ({ ...p, name: true }));
                    }}
                    error={visibleErrors.name}
                />
                <TextArea
                    ref={contentRef}
                    label={t('addModal.content')}
                    placeholder={t('addModal.contentPlaceholder')}
                    description={t('addModal.contentDescription')}
                    value={content}
                    onChange={(e) => {
                        setContent(e.target.value);
                        if (!touched.content) setTouched((p) => ({ ...p, content: true }));
                    }}
                    error={visibleErrors.content}
                    minRows={5}
                    autosize
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                />
                <div className="flex justify-end gap-2 mt-2">
                    <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                        {t('addModal.cancel')}
                    </Button>
                    <Button onClick={handleSubmit} isLoading={isSubmitting}>
                        {t('addModal.submit')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
